import { useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  draftRoundTypes,
  formatDraftSlotLine,
  formatDraftValue,
  MAX_CYCLE_WEEKS,
  nextSlotJoinCounts,
  loadStepFor,
  referenceLoadable,
  roundReferenceKg,
  type CurrentMax,
  type SlotRef,
  type Draft,
  type DraftLineOptions,
  type Exercise,
  type UnitsPreference,
} from "@heraion/shared";

import { EntryTipModal } from "../Logger/EntryTipModal";
import { GroupAlignDialog } from "../Logger/GroupAlignDialog";
import { IconButton } from "../primitives/IconButton";
import { SheetMenu } from "../primitives/SheetMenu";
import { PrescribeNoteEditor } from "../ProgramComposer/prescribe/PrescribeNoteEditor";
import type { UiTheme } from "../theme";
import { CopyToDaysTray } from "./CopyToDaysTray";
import { exerciseMenuItems } from "./exerciseMenuItems";
import { groupLabels } from "./groupLabels";
import { SupersetRenameDialog } from "./SupersetRenameDialog";
import { NextBlockSheet } from "./NextBlockSheet";
import { RoundLadder } from "./RoundLadder";
import { RuleCard } from "./RuleCard";
import { ruleOfSlot } from "./ruleCache";
import type { RuleWords } from "./ruleWords";
import { NEXT_BLOCK_ROUNDS, type V3ProgressionActions } from "./useV3ProgressionActions";
import { dayLabel } from "./useV3DayActions";
import type { V3ExerciseActions } from "./useV3ExerciseActions";
import type { V3Navigation } from "./useV3Navigation";
import type { ProgramEditorV3Props } from "./types";
import type { V3Styles } from "./v3Styles";

// One exercise of the open day (#336): its head with back, the «?» and the
// exercise menu, the schema line over every round, the note, then the round
// ladder. Variants are shown, not edited; the save keeps them as they are.
// The «?» is the logger's tip card (PE-33); an alternative is a replace. A
// superset join with uneven set counts asks the logger's pad-or-trim question.
// «Copy to other days» opens its tray under the head (2B). Slice 5: the rule
// card above the ladder, the cells by hand marked round by round, and a typed
// cell that leaves the rule (or comes back) said in a toast, and beside a
// round at effort or at % its reference load from the MAX, read only.

/** What the «?» card reads, as the host hands it to the editor. */
export type V3TipSeams = Pick<
  ProgramEditorV3Props,
  "exerciseCatalog" | "profile" | "animationSource" | "onAnimationLoadError"
>;

export function ExerciseView({
  theme,
  styles,
  draft,
  nav,
  slotIdx,
  actions,
  lineOptions,
  cellUnit,
  resolveName,
  onReplace,
  tip,
  progression,
  words,
  onOpenRule,
  maxMeta,
  inPane = false,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  nav: V3Navigation;
  slotIdx: number;
  actions: V3ExerciseActions;
  lineOptions: DraftLineOptions;
  cellUnit: UnitsPreference;
  resolveName: (id: string, snapshot?: string) => string;
  onReplace: () => void;
  tip: V3TipSeams;
  progression: V3ProgressionActions;
  words: RuleWords;
  onOpenRule: (refs: SlotRef[]) => void;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  /** A wide screen's right pane (slice 6): the day stays in view, no back. */
  inPane?: boolean;
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [alignAsk, setAlignAsk] = useState<ReturnType<typeof nextSlotJoinCounts>>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const sIdx = nav.dayIdx;
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const slot = slots[slotIdx];
  if (!slot) return null;
  const types = draftRoundTypes(draft);
  const rec = ruleOfSlot(slot, types, loadStepFor(cellUnit));
  const hand = new Set(rec.kind === "rule" ? rec.exceptions.map((e) => e.week) : []);
  const max = maxMeta?.get(slot.exerciseId);
  const refs = max
    ? Array.from({ length: draft.cycleWeeks }, (_, w) => {
        const kg = roundReferenceKg(slot, w, max);
        if (kg === null) return null;
        const load = formatDraftValue("absolute", referenceLoadable(kg, cellUnit), lineOptions);
        return t("editorV3.rule.reference", { load, defaultValue: "ref. ≈ {{load}}" });
      })
    : undefined;
  // The set table's typing, with the notice when a cell leaves the rule.
  const ladderActions: V3ExerciseActions = {
    ...actions,
    round: (i, w) => {
      const r = actions.round(i, w);
      return {
        ...r,
        updateRow: (rowIdx, patch) => {
          progression.noticeEdit(i, w, rowIdx, patch);
          r.updateRow(rowIdx, patch);
        },
      };
    },
  };
  const name = resolveName(slot.exerciseId, slot.exerciseName);
  const catalog = tip.exerciseCatalog ?? [];
  const tag = slot.group ? groupLabels(draft, sIdx, t).get(slot.group.id) : undefined;
  const groupId = slot.group?.id;
  const variants = slot.variantsRaw
    .slice(1)
    .map((v) => resolveName(v.exerciseId, v.exerciseName))
    .join(", ");
  // A join that meets different set counts asks first; equal counts just join.
  const pair = () => {
    const counts = nextSlotJoinCounts(draft, sIdx, slotIdx);
    if (counts) setAlignAsk(counts);
    else actions.pairWithNext(slotIdx);
  };

  return (
    <View style={styles.section}>
      <View style={styles.headRow}>
        {inPane ? null : (
          <IconButton
            theme={theme}
            name="chevron-left"
            accessibilityLabel={t("editorV3.backToDay", {
              day: dayLabel(draft, sIdx),
              defaultValue: "Back to {{day}}",
            })}
            onPress={nav.closeExercise}
          />
        )}
        <Text style={styles.headTitle} accessibilityRole="header" numberOfLines={2}>
          {name}
        </Text>
        <IconButton
          theme={theme}
          name="help"
          accessibilityLabel={t("editorV3.exerciseTipA11y", { name, defaultValue: "About {{name}}" })}
          onPress={() => setTipOpen(true)}
        />
        <IconButton
          theme={theme}
          name="more"
          accessibilityLabel={t("editorV3.exerciseMenuA11y", { name, defaultValue: "{{name}}, menu" })}
          onPress={() => setMenuOpen(true)}
        />
      </View>
      {copyOpen ? (
        <CopyToDaysTray
          theme={theme}
          draft={draft}
          sIdx={sIdx}
          what={name}
          onCopy={(days) => {
            actions.copyToDays([slot.id], days);
            setCopyOpen(false);
          }}
          onCancel={() => setCopyOpen(false)}
        />
      ) : null}
      {tag !== undefined ? <Text style={styles.tag}>{tag}</Text> : null}
      <Text
        style={styles.line}
        accessibilityLabel={formatDraftSlotLine(slot, draft.cycleWeeks, lineOptions, true) ?? ""}
      >
        {formatDraftSlotLine(slot, draft.cycleWeeks, lineOptions) ?? ""}
      </Text>
      {variants ? (
        <Text style={styles.muted}>
          {t("editorV3.variants", {
            names: variants,
            defaultValue: "Alternates with {{names}}. Not editable here yet.",
          })}
        </Text>
      ) : null}
      {noteOpen ? (
        <PrescribeNoteEditor
          theme={theme}
          value={slot.instructions}
          onChange={(text) => actions.setNote(slotIdx, text)}
          placeholder={t("composer.exerciseNotePlaceholder", {
            defaultValue: "Note for this exercise (shown while logging)",
          })}
          onClose={() => setNoteOpen(false)}
        />
      ) : slot.instructions.trim() ? (
        <Text style={styles.muted}>{slot.instructions}</Text>
      ) : null}
      <RuleCard
        theme={theme}
        styles={styles}
        rec={rec}
        types={types}
        rounds={draft.cycleWeeks}
        words={words}
        onRealign={() => progression.realign(slotIdx)}
        onChange={() => onOpenRule([{ day: sIdx, slot: slotIdx }])}
        lockedThrough={progression.lockedThrough}
      />
      <RoundLadder
        theme={theme}
        styles={styles}
        slot={slot}
        slotIdx={slotIdx}
        cycleWeeks={draft.cycleWeeks}
        roundTypes={draft.roundTypes}
        expandedRound={nav.expandedRound}
        onToggleRound={nav.toggleRound}
        lineOptions={lineOptions}
        cellUnit={cellUnit}
        actions={ladderActions}
        hand={hand}
        onRealignRound={(w) => progression.realign(slotIdx, w)}
        refs={refs}
        lockedThrough={progression.lockedThrough}
        onAddBlock={draft.cycleWeeks + NEXT_BLOCK_ROUNDS <= MAX_CYCLE_WEEKS ? () => setBlockOpen(true) : undefined}
      />
      {blockOpen ? (
        <NextBlockSheet
          theme={theme}
          styles={styles}
          draft={draft}
          sIdx={sIdx}
          lineOptions={lineOptions}
          resolveName={resolveName}
          onAdd={progression.addBlock}
          onClose={() => setBlockOpen(false)}
        />
      ) : null}
      <SheetMenu
        theme={theme}
        visible={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={name}
        closeLabel={t("program.cancel", { defaultValue: "Cancel" })}
        items={exerciseMenuItems({
          t,
          draft,
          sIdx,
          slotIdx,
          openRound: nav.expandedRound,
          actions,
          onReplace,
          onPair: pair,
          onRename: () => setRenameOpen(true),
          onNote: () => setNoteOpen(true),
          onCopyToDays: () => setCopyOpen(true),
        })}
      />
      <SupersetRenameDialog
        theme={theme}
        styles={styles}
        visible={renameOpen}
        current={slot.group?.name ?? ""}
        onSave={(text) => {
          if (groupId) actions.renameSuperset(groupId, text);
        }}
        onClose={() => setRenameOpen(false)}
      />
      {alignAsk ? (
        <GroupAlignDialog
          shortest={alignAsk.shortest}
          longest={alignAsk.longest}
          onPad={() => {
            actions.pairWithNext(slotIdx, "pad");
            setAlignAsk(null);
          }}
          onTrim={() => {
            actions.pairWithNext(slotIdx, "trim");
            setAlignAsk(null);
          }}
          onCancel={() => setAlignAsk(null)}
          // #336 S1: rounds that pad or trim to different counts get no number.
          {...(alignAsk.sameInEveryRound
            ? {}
            : {
                padLabel: t("editorV3.alignPadEachRound", { defaultValue: "+ Pad to the longest in each round" }),
                trimLabel: t("editorV3.alignTrimEachRound", { defaultValue: "− Trim to the shortest in each round" }),
              })}
          hideTrimWarning
          trimHint={t("editorV3.alignTrimHint", {
            defaultValue: "Removes the extra sets at the end. Undo in the message brings them back.",
          })}
        />
      ) : null}
      <EntryTipModal
        visible={tipOpen}
        exercise={catalog.find((ex) => ex.slug === slot.exerciseId) ?? null}
        catalog={catalog}
        planNote={slot.instructions}
        onClose={() => setTipOpen(false)}
        onPickAlternative={(alt: Exercise) => {
          actions.replace(slotIdx, alt.slug);
          setTipOpen(false);
        }}
        profile={tip.profile}
        animationSource={tip.animationSource}
        onAnimationLoadError={tip.onAnimationLoadError}
      />
    </View>
  );
}
